import { throttle } from 'lodash';
import * as SMH from '../../typings';
import WeakAccessTokenManager from './weak';

const sleep = (delay: number) => new Promise(resolve => setTimeout(resolve, delay));

const getPersonalSpace = throttle(
  (
    http: SMH.HTTP,
    organizationId: SMH.OrganizationId,
    userToken: SMH.UserToken,
    body: SMH.AccessTokenManagerHttpBody,
  ) =>
    http({
      customAttributes: { apiName: 'getPersonalSpaceToken' },
      method: 'post',
      url: `/user/v1/space/${organizationId}/personal`,
      params: { user_token: userToken },
      data: body,
    }),
  5000,
  { trailing: false },
);

/**
 * @description 拥有基础能力的 AccessToken 管理器
 */
class AccessTokenManager extends WeakAccessTokenManager<SMH.SpaceToken> {
  private httpBody: SMH.AccessTokenManagerHttpBody = { spaceOrgId: undefined };

  public baseInfo: SMH.AccessTokenManagerBaseInfo = { spaceId: '' };

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(config: SMH.AccessTokenManagerConfig<SMH.SpaceToken>) {
    super(config);
  }

  public searchSpaceByAccessToken(accessToken: SMH.AccessToken): SMH.SpaceToken | undefined {
    return Object.values(this.tokenMap).find(space => space.accessToken === accessToken);
  }

  /**
   * @description 异步获取个人空间 SpaceId，
   * 异步是考虑了个人空间 SpaceId 为空、需要从 server 获取的情况，
   * 同步获取可直接访问 baseInfo.personalSpaceId。
   */
  public async getPersonalSpaceIdAsync(): Promise<SMH.SpaceId> {
    let { spaceId } = this.baseInfo;
    if (!spaceId) {
      const personalSpace = await this.fetch();
      spaceId = personalSpace.spaceId;
    }
    return spaceId;
  }

  /**
   * @description 从 server 获取 spaceId，并更新 tokenMap 与个人空间的 spaceId 且返回
   * @param spaceId 允许为空，空则代表个人空间
   * @returns space
   * @memberof AccessTokenManager
   */
  public async fetch(spaceId?: SMH.SpaceId, cycles = 0, spaceOrgId?: number): Promise<SMH.SpaceToken> {
    if (!this.http) {
      throw new Error('[SMH Warning] No http function.');
    }
    const { organizationId, userToken } = this.httpParams;
    if (!organizationId || !userToken) {
      if (cycles < 4) {
        await sleep(750);
        const space = await this.fetch(spaceId, cycles + 1, spaceOrgId);
        return space;
      }
      throw new Error('[SMH Warning] No organizationId or userToken.');
    }
    let space = this.tokenMap[spaceId || this.baseInfo.spaceId];

    let bodyData: SMH.AccessTokenManagerHttpBody = {};
    if (spaceOrgId && typeof spaceOrgId === 'number') {
      // 外部传入 spaceOrgId
      bodyData = { spaceOrgId };
    } else if (typeof space?.spaceOrgId === 'number') {
      // 取缓存中的 orgId
      bodyData = { spaceOrgId: space.spaceOrgId };
    } else {
      // 取全局唯一的 orgId
      bodyData = this.httpBody;
    }
    // 从 server 获取 space。若无 spaceId 则认为是个人空间。
    if (!spaceId) {
      const res = await getPersonalSpace(this.http, organizationId, userToken, bodyData);
      if (res) {
        space = res.data;
        this.baseInfo.spaceId = space.spaceId;
      }
    } else {
      const res = await this.http({
        customAttributes: { apiName: 'getSpaceToken' },
        method: 'post',
        url: `/user/v1/space/${organizationId}/token/${spaceId}`,
        params: { user_token: userToken },
        data: bodyData,
      });
      space = res.data;
    }
    // 更新 tokenMap 中对应的的 space
    this.tokenMap[space.spaceId] = {
      ...space,
      updateTime: new Date().getTime() / 1000,
      spaceOrgId: bodyData.spaceOrgId,
    };
    this.storage.set(this.tokenMap);

    return { ...this.tokenMap[space.spaceId] };
  }

  /**
   * @description 根据 spaceId 获取 accessToken
   * 1. spaceId 允许为空，空则代表个人空间
   * 2. 若对应的 space 存在，则一定返回一个可用的 accessToken
   * @param spaceId
   * @returns
   * @memberof AccessTokenManager
   */
  public async get(spaceId?: SMH.SpaceId, spaceOrgId?: number): Promise<SMH.SpaceToken> {
    // 获取一个可用的 spaceId
    let SpaceId = spaceId;
    if (!SpaceId) {
      SpaceId = await this.getPersonalSpaceIdAsync();
    }
    // 获取对应的 space 并检验其中的 accessToken 是否有效
    let space = this.tokenMap[SpaceId];
    if (!this.isAccessTokenValid(space)) {
      space = await this.fetch(SpaceId, 0, spaceOrgId);
    }

    space.spaceOrgId = space?.spaceOrgId || this.httpBody.spaceOrgId;
    return space;
  }

  /**
   * @description get 方法的同步版本，需注意：
   * 1. 该方法不保证能返回一个有效的 accessToken
   * 2. 若 accessToken 无效，该方法也会调用刷新 accessToken，故可在使用时轮询
   * @param spaceId
   * @returns
   * @memberof AccessTokenManager
   */
  public getSync(spaceId?: SMH.SpaceId): SMH.SpaceToken {
    const SpaceId = spaceId || this.baseInfo.spaceId;
    const space = this.tokenMap[SpaceId];
    // 仍需检验对应的 SpaceId 是否有效，若无效则及时更新，已备后续使用
    if (!this.isAccessTokenValid(space)) {
      this.fetch(SpaceId);
    }
    return space;
  }

  clear(): void {
    this.tokenMap = {};
    this.baseInfo = { spaceId: '' };
    this.storage.del();
  }
}

export default AccessTokenManager;
